import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import { colourNameToHex } from "@/utils/helpers";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { setSelectedSKU } from "@/store/slices/homeSlice";
import { HomeProduct } from "@/types/home";

interface VarientSelectionProps {
  expanded: string | boolean;
  handleChange: (
    id: string
  ) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  product: HomeProduct;
}

const VarientSelection: React.FC<VarientSelectionProps> = ({
  expanded,
  handleChange,
}) => {
  const dispatch = useAppDispatch();
  const { productDetail, selectedSKU, loading } = useAppSelector(
    (state) => state.home
  );

  const [selectedSize, setSelectedSize] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<string>("");

  const skuDetails = useMemo(
    () => productDetail?.products[0]?.skuDetails || [],
    [productDetail?.products]
  );

  // Get all available sizes and colors
  const allSizes = [...new Set(skuDetails.map((sku) => sku.size_name))];
  const allColors = [...new Set(skuDetails.map((sku) => sku.colour_name))];

  // Find matching sizes and colors based on selection
  const availableSizes = selectedColor
    ? skuDetails
        .filter((sku) => sku.colour_name === selectedColor)
        .map((sku) => sku.size_name)
    : allSizes;

  const availableColors = selectedSize
    ? skuDetails
        .filter((sku) => sku.size_name === selectedSize)
        .map((sku) => sku.colour_name)
    : allColors;

  // Auto-select a valid size and color when the SKU is preselected
  useEffect(() => {
    if (selectedSKU) {
      const matchedSKU = skuDetails.find((sku) => sku.SKU_ID === selectedSKU);
      if (matchedSKU) {
        setSelectedSize(matchedSKU.size_name || "");
        setSelectedColor(matchedSKU.colour_name || "");
      }
    }
  }, [selectedSKU, skuDetails]);

  // Dispatch setSelectedSKU when size or color changes
  useEffect(() => {
    if (!skuDetails.length) return; // Prevent execution if no SKU data

    let matchedSKU;

    if (selectedSize && selectedColor) {
      // Find SKU matching both selected size and color
      matchedSKU = skuDetails.find(
        (sku) =>
          sku.size_name === selectedSize && sku.colour_name === selectedColor
      );
    }

    if (!matchedSKU && selectedSize) {
      // Find first SKU with the selected size (if color isn't found)
      matchedSKU = skuDetails.find((sku) => sku.size_name === selectedSize);
    }

    if (!matchedSKU && selectedColor) {
      // Find first SKU with the selected color (if size isn't found)
      matchedSKU = skuDetails.find((sku) => sku.colour_name === selectedColor);
    }

    if (matchedSKU) {
      dispatch(setSelectedSKU(matchedSKU.SKU_ID)); // Set selected SKU in Redux store
    } else {
      dispatch(setSelectedSKU(null)); // Reset SKU if no match is found
    }
  }, [selectedSize, selectedColor, dispatch, skuDetails]);

  // Automatically adjust color if size is selected but current color is unavailable
  const handleSizeChange = (size: string) => {
    setSelectedSize(size);

    if (!availableColors.includes(size)) {
      // Find the first available color for the selected size
      const newColor = skuDetails.find(
        (sku) => sku.size_name === size
      )?.colour_name;
      setSelectedColor(newColor || "");
    }
  };

  // Automatically adjust size if color is selected but current size is unavailable
  const handleColorChange = (color: string) => {
    setSelectedColor(color);

    if (!availableSizes.includes(color)) {
      // Find the first available size for the selected color
      const newSize = skuDetails.find(
        (sku) => sku.colour_name === color
      )?.size_name;
      setSelectedSize(newSize || "");
    }
  };

  return (
    <Accordion
      expanded={expanded === "variants" || expanded === ""}
      onChange={() => handleChange("variants")}
      sx={{
        border: "1px solid #d9d9d9",
        borderRadius: "6px",
        boxShadow: "none",
        marginBottom: "10px",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight="bold" fontSize="15px">
          Variants
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100px"
          >
            <CircularProgress size={25} sx={{ color: "var(--color-blue)" }} />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={3} width="100%">
            {/* Size Selection */}
            {allSizes?.length > 0 && allSizes?.[0] !== null && (
              <Box width={{ xs: "100%", sm: "300px" }}>
                <FormControl fullWidth>
                  <InputLabel>Select Size</InputLabel>
                  <Select
                    value={selectedSize}
                    onChange={(e) => handleSizeChange(e.target.value)}
                    label="Select Size"
                  >
                    {allSizes.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Color Selection */}
            {allColors?.length > 0 && allColors?.[0] !== null && (
              <Box display="flex" flexDirection="column">
                <Typography variant="body2" fontWeight={600} mb={1}>
                  Select Color
                </Typography>

                <Box
                  display="grid"
                  gridTemplateColumns={{
                    xs: "repeat(auto-fit, minmax(60px, 1fr))",
                    sm: "repeat(auto-fit, minmax(70px, 1fr))",
                  }}
                  gap={2}
                  maxHeight="200px"
                  overflow="auto"
                  sx={{
                    pr: 1,
                  }}
                >
                  {allColors.map((color) => {
                    const isAvailable = availableColors.includes(color);
                    const colorHex = colourNameToHex(color.toLowerCase());

                    return (
                      <Tooltip title={color} key={color}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="start"
                          minWidth="100px"
                          paddingX={2}
                          onClick={() =>
                            isAvailable && handleColorChange(color)
                          }
                          sx={{
                            cursor: isAvailable ? "pointer" : "not-allowed",
                            opacity: isAvailable ? 1 : 0.3,
                            padding: 1,
                            borderRadius: 1,
                            transition: "0.2s",
                            
                          }}
                        >
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: "50%",
                              backgroundColor: colorHex,
                              outline:
                                selectedColor === color
                                  ? "2px solid var(--color-blue)"
                                  : "1px solid gray",
                              outlineOffset: 1,
                              mb: 0.5,
                            }}
                          />
                          <Typography
                            variant="caption"
                            fontWeight="500"
                            textAlign="center"
                            
                          >
                            {color}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default VarientSelection;